import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import FloorModal from '../../../components/floor/FloorModal';
import { Topic } from '../../../components/item-content-two/Topic';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import SimpleItemCard from '../../../components/item-content/SimpleItemCard';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import TableTab from '../../../components/tabs/table-tab';
import { DEF_PG_SIZE_SUB } from '../../../constants/maximum-page-size';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { useLocale } from '../../../contexts/localeContext';
import { TableContextProvider } from '../../../contexts/table/TableContextProvider';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import { Building as BuildingModel } from '../../../models/Building';
import floorColumn from '../../../models/ColumnDefinitions/FloorColumn';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { Floor } from '../../../models/Floor';
import { ListData } from '../../../models/ListData';
import { ListQuery } from '../../../models/ListQuery';
import { Lookup } from '../../../models/Lookup';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { TabSummary } from '../../../models/TabSummary';
import floorSearch from '../../../models/search/FloorSearch';
import { DeleteEntityResult } from '../../../models/services/DeleteEntityResult';
import { removeColumns } from "../../../models/utilities/columnHelper";
import { getBuildingById, getBuildingNamesWithPagination, storeBuilding } from '../../../services/buildingService';
import { assignFloor, deleteFloor, getFloorById, getFloors } from '../../../services/floorService';
import { getSiteNamesWithPagination } from '../../../services/siteService';
import { getErrorMessage, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from '../../../store/types';
import { appendFilter } from '../../../utilities/filters';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const allItemsPath = '/access-control/hardware/buildings';
const itemPath = '/access-control/hardware/building/';

const initialItem: BuildingModel = {
    id: 0,
    name: '',
    site: '',
    siteId: 0,
    description: '',
}

const Building: FC = () => {
    const { translate } = useLocale();
    const initialTabs =
        [
            { label: translate.byKey(TranslationKeys.floors), badge: 0, showInitial: true, isActive: true },
            { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true }
        ];

    const defaultFloor = {
        id: 0,
        name: '',
        level: '',
        description: '',
        floors: 0,
        lifts: 0,
        siteId: 0,
        site: '',
        default: undefined
    }
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<BuildingModel>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>([])
    const [showManagedModal, setShowManagedModal] = useState(false)
    const [currentFloorItem, setCurrentFloorItem] = useState<Floor>(defaultFloor);
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const lookup: Lookup[] = [
        {
            name: 'building.site.siteName',
            values: [],
            dataMethod: getSiteNamesWithPagination
        }
    ];

    const editAssignedFloor = async (id: string) => {
        try {
            const assignedFloor = await getFloorById(id);
            setCurrentFloorItem(assignedFloor)
            setShowManagedModal(true);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { setShowManagedModal(false) });
        }
    }

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteFloor(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        const updatedTabs = updateTabBadgeTotal(0, tabs[0].badge - 1, tabs);
                        setTabs(updatedTabs);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }
    const getNewFloorsForABuilding = (listParameters) => {
        return getFloors({
            ...listParameters, filters: appendFilter(itemId, "Building", 'building.id', listParameters?.filters)
        })
    }

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    const tabContentList: ContentWrapper[] = [{
        content: <TableContextProvider
            dataMethod={getNewFloorsForABuilding}
            tableDescriptor={removeColumns(floorColumn, ['site', 'building', 'deviceName', 'actionName'])}
            searchDescriptor={removeColumns(floorSearch, ['site', 'building', 'deviceName', 'actionName'])}
            useFilter
            useSort
            usePagination
            useSearch
            useTable
            editMethod={editAssignedFloor}
            manualDataRefresh={manualDataRefresh}
            deleteMethod={onDelete}
            maxPageSize={DEF_PG_SIZE_SUB}
            lookup={lookup}>
            <TableTab
                title={translate.byKey(TranslationKeys.assignedFloors)}
                setShowModal={setShowManagedModal}
                selectRows={false}
                cyName="building">
                <button id='manageFloorsBtn' type="button" className="flex px-4 bg-lime rounded-lg shadow text-white text-sm items-center"
                    onClick={() => setShowManagedModal(true)}>
                    {translate.byKey(TranslationKeys.manage)}
                </button>
            </TableTab>
        </TableContextProvider >

    },
    {
        content: <Description title={translate.byKey(TranslationKeys.notes)} />
    }]

    const topic: Topic = {
        accessor: 'buildingName',
        title: translate.byKey(TranslationKeys.buildings),
        allItemsPath: allItemsPath,
        itemPath: itemPath,
    }

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    const reloadPage = async () => {
        try {
            let building;
            setTabs([]);
            if (itemId) {
                building = await getBuildingById(itemId);
                setSelectedItem({ label: building.name, value: building.id.toString(), isDefault: building.default })
                setCurrentItem(building);
                const updatedTabs = updateTabBadgeTotal(0, building?.floors, initialTabs);
                setTabs(updatedTabs);
                dataRefresh((query) => getNewFloorsForABuilding(query));
            }
            else {
                setCurrentItem(initialItem)
                setTabs([initialTabs[1]]);
            }
            setBreadcrumb([
                { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.buildings) },
                { value: itemPath + `${itemId}`, displayName: `${building?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    useEffect(() => {
        reloadPage();
    }, [])

    useEffect(() => {
        setTabs([]);
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const getTabContent = (index?: number) => {
        const selectedTabContent = itemId ? tabContentList[index ?? 0] : tabContentList[1];
        return selectedTabContent.content;
    };

    const saveBuilding = (values) => {
        const building: BuildingModel = {
            ...currentItem,
            id: +itemId,
            name: values.name,
            siteId: itemId ? values.siteId : siteId,
            description: values.description
        }

        const isNew = itemId ? +itemId < 0 : true;
        const storeMethod = storeBuilding;

        saveItem(setBusy, storeMethod, building, isNew, setItemId, history);
    }

    const onModalClose = () => {
        setCurrentFloorItem(defaultFloor);
        setShowManagedModal(false);
    }

    const saveFloor = (values) => {
        setBusy(true);
        const floor: Floor = {
            ...currentFloorItem,
            id: values.id ?? 0,
            name: values.name,
            level: values.level,
            description: values.description,
            buildingId: itemId
        }
        const isNew = values.id ? false : true;

        assignFloor(floor, isNew)
            .then(async () => {
                reloadPage();
                toast.success(translate.byKey(TranslationKeys.floorSaved));
            })
            .catch((e) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(e) });
            })
            .finally(() => {
                setCurrentFloorItem(defaultFloor);
                setBusy(false);
                setShowManagedModal(false)
            })
    }

    const saveItem = (setBusy, storeMethod, item, createNewItem, setItemId, history) => {
        setBusy(true);
        storeMethod(item, createNewItem)
            .then(async (value) => {
                setCurrentItem(item);
                setItemId(value.id);
                history.push(`${itemPath}${value.id}`);
                toast.success(translate.byKey(TranslationKeys.buildingSaved));
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }

    return <>
        <TopicContextProvider topic={topic} dataMethod={getBuildingNamesWithPagination} itemId={itemId} selectedItem={selectedItem}>
            <ItemContentTabTwo>
                <SimpleItemDetails
                    breadcrumb={breadcrumb}
                    busy={busy}
                    getTabContent={getTabContent}
                    itemProfile={currentItem}
                    onBack={() => history.push(allItemsPath)}
                    saveDetails={saveBuilding}
                    tabs={tabs}
                    itemPath={itemPath}
                    initialValues={currentItem}>
                    <SimpleItemCard />
                </SimpleItemDetails>
            </ItemContentTabTwo>
        </TopicContextProvider>
        <FloorModal isOpen={showManagedModal} onClose={onModalClose} saveFloor={saveFloor} initialValues={currentFloorItem} />
    </>
}

export default Building;
